/**
 * Drill canvas tool palette — the set of placeable items on a drill.
 *
 * Mirrors web1's EntityTray ToolMode union, scoped to what the drill
 * canvas page supports today. Players (home/away/official + ball) are
 * still placed via Josh's TacticsBoard2D / RosterPicker; equipment is
 * placed via this canvas's own overlay layer.
 */

import type { DrillCone } from '@4cm/core/drills/types';

/** Subset of DrillCone.variant that the equipment palette exposes. */
export type EquipmentVariant = NonNullable<DrillCone['variant']>;

export interface EquipmentTool {
  id: EquipmentVariant;
  label: string;
  /** Single-character / glyph icon for the tray button. */
  icon: string;
  /** Hex tint for the visual on the canvas. Falls back to the renderer
   *  default when omitted (cones → orange, goals → white, etc.). */
  tint?: string;
  /** Default rotation in degrees, 0 = facing the attacking goal. */
  defaultRotation?: number;
}

export interface EquipmentGroup {
  label: string;
  tools: EquipmentTool[];
}

/** The palette grouping the tray renders. */
export const EQUIPMENT_GROUPS: EquipmentGroup[] = [
  {
    label: 'Markers',
    tools: [
      { id: 'cone', label: 'Cone', icon: '▲', tint: '#F39C12' },
      { id: 'flat', label: 'Disc', icon: '●', tint: '#E67E22' },
      { id: 'cone_disc', label: 'Disc cone', icon: '◉', tint: '#F1C40F' },
      { id: 'cone_training', label: 'Training cone', icon: '▴', tint: '#E67E22' },
      { id: 'cone_traffic', label: 'Traffic cone', icon: '▼', tint: '#D35400' },
      { id: 'gate', label: 'Gate', icon: '▲▲', tint: '#F39C12' },
      { id: 'corner_flag', label: 'Corner flag', icon: '⚑', tint: '#E74C3C' },
    ],
  },
  {
    label: 'Obstacles',
    tools: [
      { id: 'mannequin', label: 'Mannequin', icon: '┃', tint: '#95A5A6' },
      { id: 'pole', label: 'Pole', icon: '│', tint: '#7F8C8D' },
      { id: 'hurdle_mini', label: 'Hurdle (mini)', icon: '⏉', tint: '#9B59B6' },
      { id: 'hurdle_med', label: 'Hurdle (medium)', icon: '⏊', tint: '#8E44AD' },
      { id: 'agility_ladder', label: 'Agility ladder', icon: '☱', tint: '#1ABC9C' },
      { id: 'bench', label: 'Bench', icon: '═', tint: '#7F8C8D' },
      { id: 'rebounder', label: 'Rebounder', icon: '⌐', tint: '#34495E' },
    ],
  },
  {
    label: 'Goals',
    tools: [
      { id: 'goal_mini', label: 'Mini goal', icon: '⌒', tint: '#FFFFFF' },
      { id: 'goal_small', label: 'Small goal', icon: '⊓', tint: '#FFFFFF' },
      { id: 'goal_7v7', label: '7v7 goal', icon: '⊓7', tint: '#FFFFFF' },
      { id: 'goal_9v9', label: '9v9 goal', icon: '⊓9', tint: '#FFFFFF' },
      { id: 'goal_full', label: 'Full goal', icon: '⎍', tint: '#FFFFFF' },
      { id: 'crossbar', label: 'Crossbar', icon: '━', tint: '#FFFFFF' },
    ],
  },
];

/** Default rotation by variant — goals face the attacking goal-line by default. */
export function defaultRotationFor(variant: EquipmentVariant): number {
  switch (variant) {
    case 'goal_mini':
    case 'goal_small':
    case 'goal_7v7':
    case 'goal_9v9':
    case 'goal_full':
    case 'crossbar':
      return 180; // goals face the away end by default
    default:
      return 0;
  }
}
